import type { ReactNode } from "react";
import { useEffect } from "react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BindMobileModal from "@/components/feature/BindMobileModal";
import { useRequireBoundMobile } from "@/hooks/useRequireBoundMobile";
import { CUSTOMER_AUTH_CHANGED_EVENT } from "@/lib/app-notify";
import { auth } from "@/lib/auth";
import { buildLoginRedirect } from "@/lib/navigation";

type BindMobileScene = "therapist" | "profile" | "orders" | "assistant";

interface RequireBoundMobileProps {
  children: ReactNode;
  scene: BindMobileScene;
}

const sceneCopy: Record<BindMobileScene, { title: string; description: string }> = {
  therapist: {
    title: "绑定手机号",
    description: "绑定手机号后，我们可以为您提供更完整的技师预约服务，并及时同步服务提醒。",
  },
  profile: {
    title: "绑定手机号",
    description: "绑定手机号后，您的账号资料、订单记录和售后服务会更完整，后续联系也更顺畅。",
  },
  orders: {
    title: "绑定手机号",
    description: "绑定手机号后，我们会及时为您同步订单进度、技师联系和服务通知。",
  },
  assistant: {
    title: "绑定手机号",
    description: "绑定手机号后，助理可以结合您的服务记录和预约状态，为您提供更完整的到家服务建议。",
  },
};

export default function RequireBoundMobile({ children, scene }: RequireBoundMobileProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkingBoundMobile, bindMobileRequired, mobileBound, markMobileBound } = useRequireBoundMobile();
  const copy = sceneCopy[scene];
  const [hasToken, setHasToken] = useState(() => Boolean(auth.getToken()));

  useEffect(() => {
    const refreshTokenState = () => setHasToken(Boolean(auth.getToken()));
    window.addEventListener(CUSTOMER_AUTH_CHANGED_EVENT, refreshTokenState);
    window.addEventListener("storage", refreshTokenState);
    refreshTokenState();
    return () => {
      window.removeEventListener(CUSTOMER_AUTH_CHANGED_EVENT, refreshTokenState);
      window.removeEventListener("storage", refreshTokenState);
    };
  }, []);

  useEffect(() => {
    if (hasToken) {
      return;
    }
    navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
  }, [hasToken, location.pathname, location.search, navigate]);

  return (
    <>
      {hasToken && mobileBound ? children : null}
      {hasToken && checkingBoundMobile && !bindMobileRequired ? (
        <div className="flex min-h-screen items-center justify-center bg-[#FFF9F3] px-6 text-center text-sm font-semibold text-[#A66A36]">
          正在确认账号信息...
        </div>
      ) : null}
      <BindMobileModal
        open={bindMobileRequired}
        title={copy.title}
        description={copy.description}
        onBound={markMobileBound}
        onCancel={() => navigate("/", { replace: true })}
      />
    </>
  );
}
